import React, { FC } from 'react';
import { Grid, Column } from 'src/components/Layout/Grid';
import cx from 'classnames';
import ReactPlayer from 'react-player';

// contexts
import { useTrack } from 'src/contexts/AnalyticsContext';

// helpers
import { EMBEDDED_VIDEO_EVENTS } from 'src/lib/trackingEvents';
import { VideoEmbed as VideoEmbedProps } from './VideoEmbed.types';
import { getUrlDomain } from 'src/lib/url-helpers';
import { sanitizeHtml } from 'src/lib/html-utils';
import { ConditionalWrapper } from 'src/components/helpers';
import { Lazy } from 'src/components-v2/Helpers';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { CmsThemes } from 'src/lib/constants';

import styles from './VideoEmbed.module.scss';

const supportedVideoProviderDomains = ['youtube.com', 'vimeo.com'];

const VideoEmbed: FC<VideoEmbedProps> = ({
  isLazy = false,
  caption = '',
  className = '',
  useThreadsGrid = false,
  url,
  isInModal = false,
}) => {
  const track = useTrack();
  const cmsTheme = useCmsTheme();
  if (!supportedVideoProviderDomains.includes(getUrlDomain(url))) return null;

  return (
    <figure
      className={cx(styles.videoEmbed, className, {
        [styles.videoEmbedWeb3Theme]: cmsTheme === CmsThemes.WEB3,
      })}
    >
      <Lazy isLazy={isLazy}>
        <ConditionalWrapper
          condition={!isInModal}
          wrapper={(children) => {
            return (
              <Grid useThreadsGrid={useThreadsGrid}>
                <Column>{children}</Column>
              </Grid>
            );
          }}
          alternateWrapper={(children) => {
            return <>{children}</>;
          }} // because the grid and column mess up the modal styling
        >
          <ReactPlayer
            src={url}
            className={styles.player}
            width={null}
            height={null}
            controls={true}
            pip={true}
            data-testid='video-embed'
            onPlay={() => {
              track({
                type: EMBEDDED_VIDEO_EVENTS.ON_PLAY,
                payload: { editedUrl: url },
              });
            }}
            onPause={() => {
              track({
                type: EMBEDDED_VIDEO_EVENTS.ON_PAUSE,
                payload: { editedUrl: url },
              });
            }}
            onSeeking={() => {
              track({
                type: EMBEDDED_VIDEO_EVENTS.ON_SEEK,
                payload: { editedUrl: url },
              });
            }}
            onEnded={() => {
              track({
                type: EMBEDDED_VIDEO_EVENTS.ON_ENDED,
                payload: { editedUrl: url },
              });
            }}
            onError={() => {
              track({
                type: EMBEDDED_VIDEO_EVENTS.ON_ERROR,
                payload: { editedUrl: url },
              });
            }}
          />
          {caption && (
            <figcaption
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(caption) }}
            />
          )}
        </ConditionalWrapper>
      </Lazy>
    </figure>
  );
};

export default VideoEmbed;
